'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DesktopHeader } from '@/app/_components/DesktopHeader';
import { FloatingBnbController } from '@/app/_components/FloatingBnbController';
import { GlobalModal } from '@/app/_components/GlobalModal';
import { GlobalNavigation } from '@/app/_components/GlobalNavigation';
import { ToastProvider } from '@/app/_components/ToastProvider';
import { ToastSearchParamsController } from '@/app/_components/ToastSearchParamsController';
import { SELECTOR } from '@/constants';
import { useGlobalStore } from '@/stores/GlobalStore';
import { hasWelloBottomNav } from '@/utils';
import { HIDE_DESKTOP_HEADER_PATHNAMES } from './constants';
import { ResponsiveDialogContainer } from './ResponsiveDialogContainer';

// SSR 비활성화로 App Router 마운트 이슈 해결
const NaverCtsTracker = dynamic(() => import('./NaverCtsTracker').then(mod => ({
  default: mod.NaverCtsTracker
})), {
  ssr: false
});
interface GlobalLayoutProps {
  children: React.ReactNode;
}
const OptionalGlobalFeatureBoundary = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ErrorBoundary fallback={null}>{children}</ErrorBoundary>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const GlobalLayout = t0 => {
  const $ = _c(45);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const hideBottomNav = useGlobalStore(_temp);
  let t1;
  if ($[0] !== pathname) {
    t1 = HIDE_DESKTOP_HEADER_PATHNAMES.includes(pathname ?? "");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const showDesktopHeader = !t1;
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== hideBottomNav || $[3] !== pathname || $[4] !== showDesktopHeader) {
    const hasBottomNav = hasWelloBottomNav(pathname, hideBottomNav);
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <a href={`#${SELECTOR.MAIN}`} className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[9999] focus:rounded-small focus:bg-Gray-black focus:px-16 focus:py-8 focus:font-600 focus:text-Gray-white focus:typo-body4">본문 바로가기</a>;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    if ($[15] !== showDesktopHeader) {
      t9 = showDesktopHeader && <DesktopHeader />;
      $[15] = showDesktopHeader;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    const t10 = showDesktopHeader && "has-[article[data-header-offset-page]]:min-h-[calc(100dvh-4.5rem-1px)] mobile-tablet:has-[article[data-header-offset-page]]:min-h-dvh";
    if ($[17] !== t10) {
      t4 = cn("relative flex min-h-dvh max-w-full flex-col overflow-x-clip bg-Gray-white", t10);
      $[17] = t10;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    t5 = SELECTOR.MAIN_WRAPPER;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="sticky top-0 z-20 hidden bg-Gray-white mobile-tablet:block" id={SELECTOR.PAGE_NAV_WRAPPER} />;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <OptionalGlobalFeatureBoundary><ResponsiveDialogContainer /></OptionalGlobalFeatureBoundary>;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    T0 = ToastProvider;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <ToastSearchParamsController />;
      $[21] = t3;
    } else {
      t3 = $[21];
    }
    t2 = cn("relative flex flex-1 flex-col", {
      "mobile-tablet:pb-[calc(70px+var(--safe-area-bottom,0px))]": hasBottomNav
    });
    $[2] = hideBottomNav;
    $[3] = pathname;
    $[4] = showDesktopHeader;
    $[5] = T0;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    T0 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t10;
  if ($[22] !== children) {
    t10 = <Suspense fallback={null}>{children}</Suspense>;
    $[22] = children;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Suspense fallback={null}><OptionalGlobalFeatureBoundary><NaverCtsTracker /></OptionalGlobalFeatureBoundary></Suspense>;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t2) {
    t12 = <main className={t2} id={SELECTOR.MAIN}>{t10}{t11}</main>;
    $[25] = t10;
    $[26] = t2;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== T0 || $[29] !== t12 || $[30] !== t3) {
    t13 = <T0>{t3}{t12}</T0>;
    $[28] = T0;
    $[29] = t12;
    $[30] = t3;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <GlobalModal />;
    t15 = <GlobalNavigation />;
    $[32] = t14;
    $[33] = t15;
  } else {
    t14 = $[32];
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <OptionalGlobalFeatureBoundary><FloatingBnbController /></OptionalGlobalFeatureBoundary>;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t13 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6 || $[39] !== t7) {
    t17 = <div className={t4} id={t5}>{t6}{t7}{t13}{t14}{t15}{t16}</div>;
    $[35] = t13;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t17 || $[42] !== t8 || $[43] !== t9) {
    t18 = <>{t8}{t9}{t17}</>;
    $[41] = t17;
    $[42] = t8;
    $[43] = t9;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
};
function _temp(state) {
  return state.hideBottomNav;
}